import { useState, useEffect, useRef, useCallback } from 'react';
import { useWebRTC } from '../hooks/useWebRTC';

export function ClientPage({ socket }) {
  const [step, setStep] = useState('request');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [agentName, setAgentName] = useState('');
  const sessionRef = useRef(null); // { code, clientToken } - za nastavak posle prekida veze
  const [duration, setDuration] = useState(0);
  const [chatMessages, setChatMessages] = useState([]);
  const [pointer, setPointer] = useState(null);
  const timerRef = useRef(null);
  const canvasRef = useRef(null);
  const annotationsRef = useRef([]);

  const { startSharing, stopSharing, sharing, error, handleAnswer, handleIce } = useWebRTC(socket, code, 'client');

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    annotationsRef.current.forEach(a => drawAnnotation(ctx, a));
  }, []);

  function drawAnnotation(ctx, a) {
    ctx.strokeStyle = a.color;
    ctx.fillStyle = a.color;
    ctx.lineWidth = a.size;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (a.type === 'line') {
      ctx.beginPath();
      ctx.moveTo(a.x1, a.y1);
      ctx.lineTo(a.x2, a.y2);
      ctx.stroke();
    } else if (a.type === 'arrow') {
      drawArrow(ctx, a.x1, a.y1, a.x2, a.y2, a.color, a.size);
    } else if (a.type === 'circle') {
      const rx = Math.abs(a.x2 - a.x1) / 2;
      const ry = Math.abs(a.y2 - a.y1) / 2;
      const cx = Math.min(a.x1, a.x2) + rx;
      const cy = Math.min(a.y1, a.y2) + ry;
      ctx.beginPath();
      ctx.ellipse(cx, cy, rx || 2, ry || 2, 0, 0, Math.PI * 2);
      ctx.stroke();
    } else if (a.type === 'text') {
      ctx.font = `${a.size * 4 + 10}px sans-serif`;
      ctx.fillText(a.text, a.x, a.y);
    }
  }

  function drawArrow(ctx, x1, y1, x2, y2, color, size) {
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = size;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    const angle = Math.atan2(y2 - y1, x2 - x1);
    const headLen = 14 + size * 2;
    ctx.beginPath();
    ctx.moveTo(x2, y2);
    ctx.lineTo(x2 - headLen * Math.cos(angle - Math.PI / 6), y2 - headLen * Math.sin(angle - Math.PI / 6));
    ctx.lineTo(x2 - headLen * Math.cos(angle + Math.PI / 6), y2 - headLen * Math.sin(angle + Math.PI / 6));
    ctx.closePath();
    ctx.fill();
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
  }, [step]);

  useEffect(() => {
    if (!socket) return;

    socket.on('client:requested', ({ code: newCode, clientToken }) => {
      sessionRef.current = { code: newCode, clientToken };
      setCode(newCode);
    });
    socket.on('agent:accepted', ({ agentName: name }) => {
      setAgentName(name || 'Agent');
      setStep('connected');
    });
    socket.on('request:rejected', () => { sessionRef.current = null; setStep('ended'); });
    socket.on('error', ({ message } = {}) => { if (message) alert(message); });
    socket.on('session:ended', () => { sessionRef.current = null; setStep('ended'); stopSharing(); });

    socket.on('webrtc:answer', async ({ answer }) => { await handleAnswer(answer); });
    socket.on('webrtc:ice', async ({ candidate }) => { await handleIce(candidate); });

    socket.on('chat:message', ({ text, from }) => {
      setChatMessages(prev => [...prev, { text, from, time: new Date() }]);
    });

    socket.on('annotation', ({ data }) => {
      if (data.type === 'pointer') {
        setPointer({ x: data.x, y: data.y, w: data.w, h: data.h });
      } else if (data.type === 'pointer:hide') {
        setPointer(null);
      } else {
        // Agent salje normalizovane koordinate (0-1) - skaliraj na trenutni ekran
        const canvas = canvasRef.current;
        if (canvas) {
          const scaleX = canvas.width;
          const scaleY = canvas.height;
          const scaled = { ...data };
          if (data.x1 !== undefined) { scaled.x1 *= scaleX; scaled.y1 *= scaleY; scaled.x2 *= scaleX; scaled.y2 *= scaleY; }
          if (data.x !== undefined) { scaled.x *= scaleX; scaled.y *= scaleY; }
          annotationsRef.current.push(scaled);
        } else {
          annotationsRef.current.push(data);
        }
        redraw();
        setTimeout(() => {
          annotationsRef.current = annotationsRef.current.filter(a => a !== data);
          redraw();
        }, 8000);
      }
    });

    socket.on('annotation:clear', () => {
      annotationsRef.current = [];
      setPointer(null);
      redraw();
    });

    return () => {
      socket.off('client:requested');
      socket.off('agent:accepted');
      socket.off('request:rejected');
      socket.off('error');
      socket.off('session:ended');
      socket.off('webrtc:answer');
      socket.off('webrtc:ice');
      socket.off('chat:message');
      socket.off('annotation');
      socket.off('annotation:clear');
    };
  }, [socket, stopSharing, handleAnswer, handleIce, redraw]);

  useEffect(() => {
    if (sharing) {
      timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
    } else {
      clearInterval(timerRef.current);
      setDuration(0);
    }
    return () => clearInterval(timerRef.current);
  }, [sharing]);

  const formatDuration = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  // Posle prekida veze socket.io se sam ponovo poveze - nastavi isti zahtev tokenom
  useEffect(() => {
    if (!socket) return;
    const onReconnect = () => {
      const sess = sessionRef.current;
      if (sess) socket.emit('client:request', { code: sess.code, clientToken: sess.clientToken, description });
    };
    socket.on('connect', onReconnect);
    return () => socket.off('connect', onReconnect);
  }, [socket, description]);

  const handleRequest = () => {
    if (!socket) return;
    socket.emit('client:request', { description: description.trim(), deviceModel: 'Browser' });
    setStep('waiting');
  };

  const handleCancel = () => {
    if (socket && code) socket.emit('client:cancel-request', { code });
    sessionRef.current = null;
    setCode('');
    setStep('request');
  };

  const handleStartSharing = async () => {
    await startSharing();
    if (!error) setStep('sharing');
  };

  const handleStop = () => {
    stopSharing();
    if (socket && code) socket.emit('session:end', { code });
    sessionRef.current = null;
    setStep('ended');
  };

  const s = {
    wrap: { minHeight: '100vh', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'sans-serif' },
    card: { background: '#fff', borderRadius: 16, padding: '32px 28px', width: '100%', maxWidth: 380, boxShadow: '0 2px 20px rgba(0,0,0,0.08)', position: 'relative', zIndex: 10 },
    title: { fontSize: 20, fontWeight: 600, color: '#111', marginBottom: 6 },
    sub: { fontSize: 14, color: '#666', marginBottom: 24 },
    input: { width: '100%', padding: '12px 14px', fontSize: 22, letterSpacing: 8, textAlign: 'center', border: '1px solid #ddd', borderRadius: 10, outline: 'none', marginBottom: 12 },
    btn: { width: '100%', padding: '13px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer' },
    btnRed: { width: '100%', padding: '13px', background: '#E24B4A', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer', marginTop: 10 },
    statusBadge: (live) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 20, fontSize: 13, fontWeight: 500, background: live ? '#E1F5EE' : '#f0f0f0', color: live ? '#085041' : '#666', marginBottom: 20 }),
    dot: (live) => ({ width: 7, height: 7, borderRadius: '50%', background: live ? '#1D9E75' : '#aaa', animation: live ? 'pulse 1.2s ease-in-out infinite' : 'none' }),
    chat: { marginTop: 16, maxHeight: 120, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 },
    chatMsg: { background: '#E6F1FB', color: '#0C447C', padding: '7px 12px', borderRadius: 8, fontSize: 13 },
    privacy: { background: '#E1F5EE', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#085041', marginTop: 16 }
  };

  return (
    <div style={s.wrap}>
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.3}}`}</style>

      {/* Canvas overlay za anotacije */}
      <canvas ref={canvasRef} style={{
	  position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 9998,
	  display: step === 'sharing' ? 'block' : 'none'
	}} />

      {/* Laser pointer overlay */}
      {pointer && step === 'sharing' && (
        <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 9999 }}>
          <div style={{
            position: 'absolute',
            left: `${pointer.x * 100}%`,
	    top: `${pointer.y * 100}%`,            
	    transform: 'translate(-50%, -50%)',
            width: 28, height: 28, borderRadius: '50%',
            background: 'rgba(226,75,74,0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#E24B4A' }} />
          </div>
        </div>
      )}

      {step === 'request' && (
        <div style={s.card}>
          <div style={s.title}>Podrška za ekran</div>
          <div style={s.sub}>Ukratko opišite problem i pošaljite zahtev agentu podrške</div>
          <textarea style={{ ...s.input, fontSize: 14, letterSpacing: 0, textAlign: 'left', minHeight: 70, resize: 'vertical', fontFamily: 'inherit' }}
            value={description} onChange={e => setDescription(e.target.value)} placeholder="Opis problema (opciono)" maxLength={500} />
          <button style={s.btn} onClick={handleRequest} disabled={!socket}>Pošalji zahtev</button>
          <div style={s.privacy}>Vaš ekran je vidljiv samo agentu. Agent ne može da kontroliše vaš uređaj.</div>
        </div>
      )}

      {step === 'waiting' && (
        <div style={s.card}>
          <div style={s.statusBadge(false)}>
            <div style={s.dot(false)} />
            Čekam agenta
          </div>
          <div style={s.title}>Vaš kod: {code ? `${code.slice(0, 3)} ${code.slice(3)}` : '…'}</div>
          <div style={s.sub}>Zahtev je poslat. Sačekajte da ga agent prihvati.</div>
          <button style={{ ...s.btn, background: '#f0f0f0', color: '#333' }} onClick={handleCancel}>Otkaži zahtev</button>
        </div>
      )}

      {step === 'connected' && (
        <div style={s.card}>
          <div style={s.statusBadge(false)}>
            <div style={s.dot(false)} />
            Povezano — čeka deljenje
          </div>
          <div style={s.title}>Spremi za podršku</div>
          <div style={s.sub}>Agent {agentName} je spreman. Pokrenite deljenje ekrana.</div>
          {error && <div style={{ color: '#E24B4A', fontSize: 13, marginBottom: 12 }}>{error}</div>}
          <button style={s.btn} onClick={handleStartSharing}>Podeli ekran</button>
          <div style={s.privacy}>Browser će tražiti dozvolu za deljenje — izaberite <strong>„Ceo ekran"</strong> (ne karticu ili prozor), da bi agent video sve što vidite.</div>
        </div>
      )}

      {step === 'sharing' && (
        <div style={s.card}>
          <div style={s.statusBadge(true)}>
            <div style={s.dot(true)} />
            Deliš ekran · {formatDuration(duration)}
          </div>
          <div style={s.title}>Sesija u toku</div>
          <div style={s.sub}>Agent vidi vaš ekran i može da vam pokaže gde da kliknete.</div>

          {chatMessages.length > 0 && (
            <div style={s.chat}>
              {chatMessages.map((m, i) => (
                <div key={i} style={s.chatMsg}>
                  <strong>Agent:</strong> {m.text}
                </div>
              ))}
            </div>
          )}

          <button style={s.btnRed} onClick={handleStop}>Zaustavi deljenje</button>
        </div>
      )}

      {step === 'ended' && (
        <div style={s.card}>
          <div style={s.title}>Sesija završena</div>
          <div style={s.sub}>Hvala što ste koristili podršku.</div>
          <button style={s.btn} onClick={() => { setStep('request'); setCode(''); setDescription(''); }}>Nova sesija</button>
        </div>
      )}
    </div>
  );
}